import React from "react";
import { Button, Input, Space, Switch, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import TtsAudioControls from "../TtsAudioControls";
import { generateOptionId } from "../gameTypes";

type TextInputGame = Extract<Game, { type: "text_input" }>;

type Props = {
  game: TextInputGame;
  onChange: (game: TextInputGame) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const TextInputEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
}) => {
  // Defensive: partial/legacy games may arrive without a correctAnswers array.
  // Fall back to an empty list so the map() below doesn't crash.
  const correctAnswers: TextInputGame["correctAnswers"] = Array.isArray(
    game.correctAnswers,
  )
    ? game.correctAnswers
    : [];

  const updateVariation = (
    id: string,
    patch: Partial<TextInputGame["correctAnswers"][number]>,
  ) => {
    onChange({
      ...game,
      correctAnswers: correctAnswers.map((v) =>
        v.id === id ? { ...v, ...patch } : v,
      ),
    });
  };

  const addVariation = () => {
    onChange({
      ...game,
      correctAnswers: [
        ...correctAnswers,
        { id: generateOptionId(), text: "" },
      ],
    });
  };

  const removeVariation = (id: string) => {
    onChange({
      ...game,
      correctAnswers: correctAnswers.filter((v) => v.id !== id),
    });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>
      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>
      <div
        style={{
          padding: 12,
          border: "1px solid var(--bo-card-border)",
          borderRadius: 6,
        }}
      >
        <label style={{ fontWeight: 600 }}>Respuestas de referencia</label>
        {/* SCRUM-1123: la respuesta del alumno la califica la IA. Estas
            variaciones son ejemplos de formas aceptadas de la única respuesta
            correcta, no una lista cerrada que el alumno deba igualar. */}
        <Typography.Paragraph type="secondary" style={{ margin: "4px 0 0" }}>
          La IA compara la respuesta del alumno con estas variaciones y otorga
          1 punto (mismo dato), 0.5 (dudoso) o 0 (otro dato). No hace falta
          listar cada forma de escribirla: la ortografía, el orden y las
          palabras de más no restan salvo que se active un interruptor de abajo.
        </Typography.Paragraph>
        {correctAnswers.map((variation, idx) => (
          <div key={variation.id} style={{ marginTop: 12 }}>
            <label>Respuesta de referencia {idx + 1}</label>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginTop: 4,
              }}
            >
              <Input
                value={variation.text}
                onChange={(e) =>
                  updateVariation(variation.id, { text: e.target.value })
                }
                placeholder="Escribe la respuesta esperada"
              />
              <TtsAudioControls
                text={variation.text}
                audioUrl={variation.audio}
                onAudioChange={(url) =>
                  updateVariation(variation.id, { audio: url })
                }
                generateAudio={generateAudio}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeVariation(variation.id)}
                disabled={correctAnswers.length <= 1}
              />
            </div>
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={addVariation}
          >
            Añadir respuesta de referencia
          </Button>
        </div>
        <div
          style={{
            marginTop: 16,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Switch
              size="small"
              checked={!!game.caseSensitive}
              onChange={(checked) =>
                onChange({ ...game, caseSensitive: checked })
              }
            />
            <span>Distinguir mayúsculas y minúsculas</span>
          </div>
          {/* SCRUM-1109: apagado (default) ignora tildes faltantes, sobrantes
              o mal puestas en todas las variaciones; encendido las exige.
              Independiente del switch de mayúsculas. Ambos interruptores le
              indican a la IA qué dimensión cuenta como error (SCRUM-1123). */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Switch
              size="small"
              checked={!!game.accentSensitive}
              onChange={(checked) =>
                onChange({ ...game, accentSensitive: checked })
              }
            />
            <span>Evaluar acentuación</span>
          </div>
        </div>
      </div>
    </Space>
  );
};

export default TextInputEditor;
